import { useCallback, useMemo, useState } from 'react';
import { excelToJson } from '../../common/common.helpers';
import { CommonProperties } from '../../common/common.interfaces';
import CustomError, { CustomErrorProps } from '../../common/components/CustomError';
import { useLoading } from '../../loading/LoadingContext';
import { useUserContext } from '../../user/UserContext';
import { CloudUpload, Save } from 'react-bootstrap-icons';
import { FileUploader } from 'react-drag-drop-files';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { apiFetch } from '../../utility/fetch';
import { MapperConfigurationTableRow, MapperConfigurationSet, MapperConfigurationRelation, RawRow, UniversalConstructorContainer } from '../universalConstructor.interfaces';
import SimpleCrystalBallModal from '../../common/components/SimpleCrystalBallModal';

interface UniversalConstructorImportControlsProps extends CommonProperties {
  dataImport: any
  finishOrdersUpload: any
  container: UniversalConstructorContainer
} 
export default function UniversalConstructorImportControls (props : UniversalConstructorImportControlsProps){
  const [importError, setImportError]: [CustomErrorProps, any] = useState(undefined)
  const [integrationImportPath, setIntegrationImportPath]: [string, any] = useState('')
  const [account, setAccount]: [Record<string, string>, any] = useState({id:'', label:'SELECT AN ACCOUNT'})
  const [excelFile, setExcelFile]: [File, any] = useState(undefined)
  const [showModal, setShowModal] = useState(false)
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()

  // #region CONFIG
  const constructMapperConfigSet = useCallback(async () => {

    toggleLoading(true)
    changeStep('Starting Import')
    changeSubStep('Fetching Constructor Blueprints...')
    const response = await apiFetch<MapperConfigurationTableRow[]>({
      endpoint: 'azureTables',
      method: 'GET',
      accessToken: userData.accessToken,
      params: [
        {key: 'table', value: 'mapperConfiguration'},
        {key: 'filter', value: `PartitionKey eq '${account.id}' and version eq '100'`}
      ]
    })

    if (!response.body || response.body.length < 1) {
      throw new Error(`No table results found for account id ${account.id}`)
    }

    const configSet: MapperConfigurationSet = {
      accountId: response.body[0].partitionKey,
      version: response.body[0].version,
      name: response.body[0].name,
      listingCode: response.body[0].listingCode,
      importPath: response.body[0].importPath,
      relations: []
    }
    setIntegrationImportPath(response.body[0].importPath)

    for (const configRow of response.body) {
      const configRelation: MapperConfigurationRelation = {
        missionControlField: configRow.rowKey,
        mappedField: configRow.mappedField
      }
      configSet.relations.push(configRelation)
    }

    return configSet

  }, [account])
  
  // #region FILE PROCESSING
  const handleExcelFileUpload = useCallback(async (file: File) => {
    setExcelFile(file)
    setImportError({
      visible: false
    })
    const mapperConfigSet = await constructMapperConfigSet()
    const rows = await processExcelFile(file)
    console.log(rows)
    console.log(mapperConfigSet)
    props.dataImport(rows, mapperConfigSet)
  }, [excelFile, account])

  /**
   * I had to use this in order to delay processing the file until it's actually set
   */
  const processExcelFile = useCallback(async (file: File) => {
    changeSubStep('Aligning order materials...')
    if(!file) { // will trigger when the page is rendered or excelFile is cleared, so ignore it unless we actually have a file
      return
    }
    props.finishOrdersUpload() // clear the orders, whether we succeed or fail
    setImportError({
      visible: false
    })
    let rows = undefined
    try {
      rows = await excelToJson<RawRow>(file)
      setExcelFile(undefined)
      setImportError(undefined)
      console.log(rows)
      return rows
    }catch(error){
      setImportError({
        content: JSON.stringify(error),
        description: `Failed to load the converted JSON.`,
        title: 'UC Load Failed',
        visible: true
      } as CustomErrorProps)
      toggleLoading(false)
      return
    }
    
  }, [excelFile]);

  // #region LOAD INTO ORDER SERVICE
  const handleSaveClicked = useCallback(async () => {
    changeStep('Uploading Blobs to Order Service..')
    changeSubStep('Saving...')
    toggleLoading(true)
    
    for ( const [index, orderBlob] of props.container.ordersSuccessful.entries() ) {
      console.log(JSON.stringify(orderBlob.missionControlOrder))
      changeSubStep(`Saving... [${index+1} of ${props.container.ordersSuccessful.length}]`)
      const saveResultBlobName = `${integrationImportPath}/${orderBlob.missionControlOrder.order.Name}.json`
      const saveResultResponse = await apiFetch({
        endpoint: 'blob',
        method: 'POST',
        accessToken: userData.accessToken,
        body: JSON.stringify(orderBlob.missionControlOrder),
        params: [
          {key: 'location', value: 'dataservices'},
          {key: 'container', value: 'storage'},
          {key: 'blobname', value: saveResultBlobName}
        ]
      })
      if(!saveResultResponse.ok){
        setImportError({
          content: saveResultResponse.body,
          description: `Failed to save mapped orders file.`,
          title: 'Order Services Saving Error',
          visible: true
        } as CustomErrorProps)
        toggleLoading(false)
        return
      }
    }
    toggleLoading(false)
    props.finishOrdersUpload()
    setShowModal(true)
    return
  }, [props.container])

  const handleAccountChanged = useCallback((e) => {
    if(e.currentTarget.value === '') return
    // make this more dynamic, option only takes a string... maybe a map to go off? idk this is simple enough
    let label = ''
    if(e.currentTarget.value === '0013l00002OBc1fAAD'){
      label = 'EPIC'
    }
    if(e.currentTarget.value === '0016O00003XcljaQAB'){
      label = 'WOOT'
    }
    setAccount({id: e.currentTarget.value, label: label})
  }, [account])

  const handleClose = () => {
    setShowModal(false);
  };
  
  const handleCloseImportError = useCallback((e) => {
    setImportError({
      visible: false
    })
  }, [])

  // #region VIEW
  const view = useMemo(() => {

    let failureWarning = <></>
    if (props.container?.ordersFailed?.length > 0) {
      failureWarning = <div>Failed Orders will be ignored!</div>
    }

    return <div>
      
      {/* Input Fields */}
      <div>
        <Container>
          <Row>
            <Col xs={4}>
              <Card>
                <Card.Header>
                  <Card.Title className="app-title"><span className='beacon-header'>SELECT MARKETPLACE</span></Card.Title>
                </Card.Header>
                <Card.Body>
                  <div>Select Account</div>
                  <Form.Select
                    style={{width: '150px', height: '30px', }}
                    onChange={handleAccountChanged}
                    value={account.id}
                  >
                    <option value='' disabled>SELECT AN ACCOUNT</option>
                    <option key={1} value={'0013l00002OBc1fAAD'}>EPIC</option>
                    <option key={2} value={'0016O00003XcljaQAB'}>WOOT</option>
                  </Form.Select>
                  <span className='import-title import-block' style={{display: 'flex', alignItems: 'stretch', justifyContent: 'center'}} hidden={!account.id}>{account.label} FORMAT SELECTED</span>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <span hidden={!account.id}>
                <span style={{display: 'flex', alignItems: 'stretch', justifyContent: 'center'}}>
                  
                  {!excelFile && 
                    <FileUploader 
                        handleChange={handleExcelFileUpload} 
                        name="file" 
                        types={['xlsx','xls','csv']}
                      >
                        <div className='import-panel warning flex-fill'>
                          <div className="import-title">UPLOAD EXCEL</div>
                          <CloudUpload height={50} width={50} />
                          <div className="import-subtext">Drag & Drop / Click</div>
                        </div>
                    </FileUploader>
                  }
                  {excelFile &&
                    <FileUploader 
                        handleChange={handleExcelFileUpload} 
                        name="file" 
                        types={['xlsx','xls']}
                      >
                        <div className='import-panel success flex-fill'>
                          <div className="import-title">UPLOAD EXCEL</div>
                          <CloudUpload height={50} width={50} />
                          <div className="import-subtext">{excelFile.name}</div>
                        </div>
                    </FileUploader>
                  }
                  <Button onClick={handleSaveClicked} disabled={!(props.container?.ordersSuccessful?.length > 0)} variant='success' size='lg' className="import-panel success text-wrap fw-bold py-3 text-dark" style={{fontSize: '14px'}}><Save/>&nbsp;&nbsp;UPLOAD{failureWarning}</Button>
                </span>
              </span>
            </Col>
          </Row>
        </Container>
      </div>
      <SimpleCrystalBallModal content={<>Orders were uploaded to Order Services!<br/>Verify orders are accurate in Mission Control.</>} show={showModal} toggleModal={handleClose} />
      <hr/>
      
      
      <hr/>       
      <CustomError closeError={handleCloseImportError} content={importError?.content} description={importError?.description} title={importError?.title} visible={importError?.visible} />
    </div>
  }, [importError, props.container, account, excelFile, showModal])

  return view
}